import { MessageDirection } from '@chatscope/use-chat';
import { useState } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { v4 as uuidv4 } from 'uuid';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import * as Sentry from '@sentry/react';

type MessageState = 'SENT' | 'ANSWERING' | 'THINKING' | string;
type MessageContent = { state: MessageState; content: string };
export type Message = {
  id: string;
  response: MessageContent[];
  sender: string;
  direction: MessageDirection;
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
};

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const createUserMessage = (content: string): Message => {
  const response = [{ state: 'SENT', content }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    id: uuidv4(),
    response,
    sender: 'Me',
    direction: MessageDirection.Outgoing,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const createResponseMessage = (content?: string, id?: string): Message => {
  const response = content ? [{ state: 'ANSWERING', content }] : [];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    id: id || uuidv4(),
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const parseStreamChunk = (data: string): { state: MessageState; content: string } | null => {
  try {
    const parsed = JSON.parse(data);
    return { state: parsed.state, content: parsed.content };
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

const processContentTransforms = (content: string): string => {
  const transforms = [
    { regex: /<span\s+[^>]*style="[^"]*"[^>]*>(.*?)<\/span>/gi, replacement: '$1' },

    // Answer content is sometimes incorrectly wrapped in a <response> tag
    { regex: /<response>(.*?)<\/response>/gis, replacement: '$1' },
  ];

  return transforms.reduce(
    (transformed, { regex, replacement }) => transformed.replace(regex, replacement),
    content,
  );
};

const computeMessageProperties = (
  response: MessageContent[],
): {
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
} => {
  const thinkingContent = processContentTransforms(
    response
      .filter(r => r.state !== 'ANSWERING')
      .map(r => r.content)
      .join('\n\n'),
  );

  const answerContent = processContentTransforms(
    response
      .filter(r => r.state === 'ANSWERING')
      .map(r => r.content)
      .join('\n\n'),
  );

  const currentState = response[response.length - 1]?.state || 'SENT';

  return { thinkingContent, answerContent, currentState };
};

const appendToResponse = (message: Message, state: MessageState, content: string): Message => {
  const transformedContent = processContentTransforms(content);
  const newResponse = [...message.response];

  const lastResponse = newResponse[newResponse.length - 1];
  if (lastResponse && lastResponse.state === state) {
    newResponse[newResponse.length - 1] = {
      ...lastResponse,
      content: lastResponse.content + transformedContent,
    };
  } else {
    newResponse.push({ state, content: transformedContent });
  }

  const { thinkingContent, answerContent, currentState } = computeMessageProperties(newResponse);

  return {
    ...message,
    response: newResponse,
    thinkingContent,
    answerContent,
    currentState,
  };
};

export function useChatMessages() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationId] = useState(uuidv4());
  const [latestIncomingId, setLatestIncomingId] = useState<string | null>(null);

  const addMessage = (message: Message): Message[] => {
    const newMessages = [...messages, message];
    setMessages(newMessages);
    if (message.direction === MessageDirection.Incoming) {
      setLatestIncomingId(message.id);
    }
    return newMessages;
  };

  const handleIncomingStreamChunk = (messageId: string, rawData: string): void => {
    const parsedChunk = parseStreamChunk(rawData);
    if (!parsedChunk) return;

    const { state, content } = parsedChunk;

    setMessages(currentMessages => {
      const targetMessage = currentMessages.find(msg => msg.id === messageId);
      if (!targetMessage) {
        const newMessage = createResponseMessage();
        newMessage.id = messageId;
        const updatedMessage = appendToResponse(newMessage, state, content);
        setLatestIncomingId(messageId);
        return [...currentMessages, updatedMessage];
      }

      const updatedMessage = appendToResponse(targetMessage, state, content);
      return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
    });
  };

  const handleStreamResponse = async (response: Response): Promise<void> => {
    if (response.body === null) return;

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .pipeThrough(new EventSourceParserStream())
      .getReader();

    let hasStreamStarted = false;
    const receivedMessageId = uuidv4();

    while (true) {
      const { value, done } = await reader.read();
      if (done || value.data === '\n\n') break;

      if (!hasStreamStarted && value !== undefined) {
        hasStreamStarted = true;
      }

      if (value.data) {
        handleIncomingStreamChunk(receivedMessageId, value.data);
      }
    }
  };

  const fetchAndHandleStreamResponse = async (url: string, content: string = ''): Promise<void> => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
          conversationId,
          message: content,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch stream: ${response.status} ${errorText}`);
      }

      await handleStreamResponse(response);
    } catch (error) {
      Sentry.captureException(error);
      addMessage(createResponseMessage('An error occurred while processing your request, please wait a moment and try again. If the issue persists, please contact support.'));
    }
  };

  const sendUserMessage = async (content: string, clientId: string | null): Promise<void> => {
    Mixpanel.track(MixpanelEvents.SIESTA_MESSAGE_SENT, { message: content });

    addMessage(createUserMessage(content));

    if (!clientId) {
      addMessage(createResponseMessage('Please select a client to chat about'));
      return;
    }

    await fetchAndHandleStreamResponse(`${API_URL}/clients/${clientId}/chat`, content);
  };

  const sendActionRequest = async (action: 'agenda', clientId: string | null): Promise<void> => {
    if (!clientId) {
      addMessage(createResponseMessage('Please select a client to chat about'));
      return;
    }

    await fetchAndHandleStreamResponse(`${API_URL}/clients/${clientId}/siesta/${action}`);
  };

  return {
    messages,
    sendUserMessage,
    sendActionRequest,
    latestIncomingId,
  };
}
